// extensions/Hashtag.js
import { Mention } from "@tiptap/extension-mention";
import tippy from "tippy.js";
import { get } from "svelte/store";
import { hashtagStore, sortedHashtags } from "../stores/hashtagStore";
import { writable } from "svelte/store";

// Create a store to track the hashtag dropdown state
export const hashtagDropdownVisible = writable(false);

export const Hashtag = Mention.configure({
  HTMLAttributes: {
    class: "hashtag",
  },
  suggestion: {
    char: "#",
    command: ({ editor, range, props }) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent([
          {
            type: "mention",
            attrs: {
              id: props.id,
              label: props.label,
            },
          },
          {
            type: "text",
            text: " ",
          },
        ])
        .run();

      // Set dropdown to not visible when a hashtag is selected
      hashtagDropdownVisible.set(false);
    },
    allowSpaces: false,
    render: () => {
      let popup;
      let component;
      let selectedIndex = 0;

      return {
        onStart: (props) => {
          // Set dropdown to visible when started
          hashtagDropdownVisible.set(true);

          component = {
            props,
            onKeyDown({ event }) {
              const items = component.props.items;

              // Handle keyboard navigation
              if (event.key === "ArrowDown") {
                selectedIndex = (selectedIndex + 1) % items.length;
                updatePopup(component, null, selectedIndex);
                return true;
              }

              if (event.key === "ArrowUp") {
                selectedIndex =
                  (selectedIndex - 1 + items.length) % items.length;
                updatePopup(component, null, selectedIndex);
                return true;
              }

              if (event.key === "Enter" && items[selectedIndex]) {
                component.props.command(items[selectedIndex]);
                popup?.[0].hide();
                return true;
              }

              if (event.key === "Escape") {
                popup?.[0].hide();
                return true;
              }

              return false;
            },
          };

          popup = tippy("body", {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: renderPopup(component, selectedIndex),
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "bottom-start",
            onHidden: () => {
              // Set dropdown to not visible when hidden
              hashtagDropdownVisible.set(false);
              selectedIndex = 0;
            },
          });
        },

        onUpdate(props) {
          component.props = props;

          popup?.[0].setProps({
            getReferenceClientRect: props.clientRect,
          });

          updatePopup(component, null, selectedIndex);
        },

        onKeyDown(props) {
          return component?.onKeyDown?.(props);
        },

        onExit() {
          // Set dropdown to not visible when exited
          hashtagDropdownVisible.set(false);
          popup?.[0].destroy();
          popup = null;
        },
      };
    },
    items: ({ query }) => {
      // Get hashtags from the store
      const allHashtags = get(sortedHashtags).map((item) => item.tag);

      // Filter based on query
      const filteredTags = allHashtags
        .filter((tag) => tag.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 5)
        .map((label) => ({ id: label, label }));

      // Add a "Create new" option if the query doesn't match exactly and has content
      const exactMatch = allHashtags.some(
        (tag) => tag.toLowerCase() === query.toLowerCase(),
      );

      if (!exactMatch && query.trim()) {
        return [...filteredTags, { id: query, label: query, isNew: true }];
      }

      return filteredTags;
    },
  },
});

function renderPopup(component, selectedIndex = 0) {
  const container = document.createElement("div");
  container.classList.add("hashtag-suggestions");

  updatePopup(component, container, selectedIndex);

  return container;
}

function updatePopup(component, container = null, selectedIndex = 0) {
  const { items, command } = component.props;
  const targetContainer =
    container || document.querySelector(".hashtag-suggestions");

  if (!targetContainer) return;

  targetContainer.innerHTML = "";

  if (items.length === 0) {
    const noResults = document.createElement("div");
    noResults.classList.add("hashtag-no-results");
    noResults.textContent = "No hashtags found";
    targetContainer.appendChild(noResults);
    return;
  }

  items.forEach((item, index) => {
    const button = document.createElement("button");
    button.classList.add("hashtag-item");

    if (index === selectedIndex) {
      button.classList.add("selected");
    }

    // Style differently if it's a "create new" option
    if (item.isNew) {
      button.classList.add("create-new");
      button.innerHTML = `<span>Create:</span> <span class="tag-name">#${item.label}</span>`;
    } else {
      button.innerHTML = `<span class="tag-name">#${item.label}</span>`;
    }

    button.addEventListener("click", () => {
      command(item);
    });

    targetContainer.appendChild(button);
  });
}

// Extract hashtags from the editor content
export function extractHashtags(editor) {
  if (!editor) return [];

  const hashtagNodes = [];
  const { doc } = editor.state;

  doc.descendants((node, pos) => {
    if (node.type.name === "mention" && node.attrs.id) {
      hashtagNodes.push({
        id: node.attrs.id,
        label: node.attrs.label,
        position: pos,
      });
    }
  });

  return hashtagNodes;
}
